import {
  type Graph,
  type Thing,
  type Organization,
  type WebSite,
  type SearchAction,
} from 'schema-dts'
import { SEO_PAGE_TYPE, type SEOHeadProps } from '@/features/shared/components/SEOHeader/types'
import { SEO_CONSTANTS, type SeoSchemaImage } from '@/features/shared/utils/seo/seoConfig'
import { objectStorageUrl } from '@/features/shared/utils/objectStorageUrl'
import { socialLinks } from '@/features/shared/constants/socialLinks'

type BuildSeoJsonLdParams = {
  title: string
  description: string
  canonicalUrl: string
  image?: SeoSchemaImage
  breadcrumbs?: SEOHeadProps['breadcrumbs']
  pageTypeSchema?: SEOHeadProps['pageTypeSchema']
  additionalSchemas?: Thing[]
}

// static organization schema
const organizationSchema: Organization = {
  '@type': 'Organization',
  '@id': 'https://www.shipt.com/#organization',
  name: 'Shipt Same-day Delivery',
  url: 'https://www.shipt.com/',
  sameAs: Object.values(socialLinks),
  logo: {
    '@type': 'ImageObject',
    '@id': 'https://www.shipt.com/#logo',
    url: objectStorageUrl('seo-logo.png'),
    width: '90',
    height: '30',
    caption: 'Shipt Same-day Delivery',
  },
  image: { '@id': 'https://www.shipt.com/#logo' },
  areaServed: 'US',
  mainEntityOfPage: 'https://www.shipt.com/shop',
  contactPoint: [
    {
      '@type': 'ContactPoint',
      telephone: '+1-205-502-2500',
      contactType: 'customer service',
      contactOption: ['HearingImpairedSupported', 'TollFree'],
      areaServed: 'US',
      availableLanguage: [
        {
          '@type': 'Language',
          name: 'English',
        },
      ],
    },
  ],
  telephone: '+1-205-502-2500',
  email: 'support@shipt.com',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Birmingham, Alabama',
    postalCode: '35203',
    streetAddress: '420 20th St N, Suite 100',
  },
}

// static website schema
const websiteSchema: WebSite = {
  '@type': 'WebSite',
  '@id': 'https://www.shipt.com/#website',
  url: 'https://www.shipt.com/',
  name: 'Shipt Grocery Delivery',
  description: 'On-demand Grocery Delivery',
  publisher: { '@id': 'https://www.shipt.com/#organization' },
  potentialAction: [
    // SearchAction is a valid type from schema-dts
    // oxlint-disable-next-line typescript/consistent-type-assertions
    {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: 'https://www.shipt.com/shop/stores/search?query={search_term_string}',
      },
      'query-input': 'required name=search_term_string',
    } as SearchAction,
  ],
}

/**
 * Breadcrumbs metadata allows search engines to understand the structure of your site
 * and provide better navigation in search results. If breadcrumbs are not provided, we
 * create a default one with Home and the current page
 */
const getDefaultBreadcrumbs = ({
  title,
  canonicalUrl,
}: {
  title: string
  canonicalUrl: string
}): NonNullable<SEOHeadProps['breadcrumbs']> => ({
  '@type': 'BreadcrumbList',
  '@context': 'https://schema.org',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: 'https://www.shipt.com',
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: title,
      item: canonicalUrl,
    },
  ],
})

/**
 * Page type schema is used to define the type of page for search engines.
 * If page type schema is not provided, we create a default one with the title,
 * description, canonical URL, and image.
 */
const getDefaultPageTypeSchema = ({
  title,
  description,
  canonicalUrl,
  image,
}: {
  title: string
  description: string
  canonicalUrl: string
  image: SeoSchemaImage
}): NonNullable<SEOHeadProps['pageTypeSchema']> => ({
  '@type': [SEO_PAGE_TYPE.WEB_PAGE],
  '@context': 'https://schema.org',
  name: title,
  description,
  url: canonicalUrl,
  image,
})

/**
 * builds the full JSON-LD `@graph` shared by the pages router `SEOHead` component
 * and app router pages (via the `JsonLd` component). keeping this in a
 * single builder guarantees both routers emit identical structured data.
 */
export const buildSeoJsonLd = ({
  title,
  description,
  canonicalUrl,
  image = SEO_CONSTANTS.defaultSeoImage,
  breadcrumbs = getDefaultBreadcrumbs({ title, canonicalUrl }),
  pageTypeSchema = getDefaultPageTypeSchema({
    title,
    description,
    canonicalUrl,
    image,
  }),
  additionalSchemas = [],
}: BuildSeoJsonLdParams): Graph => ({
  '@context': 'https://schema.org',
  // additional schemas are appended as distinct nodes. they should carry their
  // own unique @id and reference the org/website rather than redefining #organization
  // or #website (same @id causes unintended merging).
  // oxlint-disable-next-line typescript/consistent-type-assertions
  '@graph': [
    organizationSchema,
    websiteSchema,
    breadcrumbs,
    { ...pageTypeSchema, inLanguage: 'en-US' },
    ...additionalSchemas,
  ] as Thing[],
})
